import { useEffect, useState } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'

import { DISTRIBUTION_SYSTEM_COPY } from '#app/routes/mocra+/_form+/components/distribution-systems/constants.ts'
import { DistributionSystemForm } from '#app/routes/mocra+/_form+/components/distribution-systems/form.tsx'
import {
	createDistributionSystemDraft,
	sanitizeDistributionSystemDraft,
	type DistributionSystemDraft,
} from '#app/routes/mocra+/_form+/components/distribution-systems/types.ts'

type DistributionSystemOverlayMode = 'add' | 'edit'

type DraftErrors = Partial<Record<keyof DistributionSystemDraft, string>>

type Props = {
	readonly mode: DistributionSystemOverlayMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: DistributionSystemDraft) => void
	readonly initialValue?: DistributionSystemDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function DistributionSystemOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<DistributionSystemDraft>(
		initialValue ? { ...initialValue } : createDistributionSystemDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(
				initialValue ? { ...initialValue } : createDistributionSystemDraft(),
			)
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open) {
			onClosed?.()
		}
	}, [onClosed, open])

	const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeDistributionSystemDraft(draft)
		const nextErrors: DraftErrors = {}
		if (!sanitized.dataset) {
			nextErrors.dataset = 'Describe the dataset or report.'
		}
		if (Object.keys(nextErrors).length > 0) {
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	const titleCopy =
		mode === 'add'
			? DISTRIBUTION_SYSTEM_COPY.title.add
			: DISTRIBUTION_SYSTEM_COPY.title.edit
	const submitCopy =
		mode === 'add'
			? DISTRIBUTION_SYSTEM_COPY.submit.add
			: DISTRIBUTION_SYSTEM_COPY.submit.edit

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={DISTRIBUTION_SYSTEM_COPY.description}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="distribution-system-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<DistributionSystemForm
				formId="distribution-system-overlay-form"
				draft={draft}
				errors={errors}
				onDraftFieldChange={(field, value) =>
					setDraft((prev) => ({ ...prev, [field]: value }))
				}
				onSubmit={handleSubmit}
			/>
		</ResponsiveOverlay>
	)
}
